import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useUserContext } from '../../context/UserContext';
import ntuCalibrationData from '../../ntu_cali.json';

const DeviceParamsForm = () => {
  const [selectedDevice, setSelectedDevice] = useState('');
  const [realTimeData, setRealTimeData] = useState({
    ts: '',
    ps1: '',
    ps2: '',
    tur: '',
    tds1_raw1: '',
    tds1_raw2: '',
    tds1_temp: '',
    tds2_raw1: '',
    tds2_raw2: '',
    tds2_temp: '',
    tds3_raw1: '',
    tds3_raw2: '',
    tds3_temp: '',
    leakage: '',
    hps: '',
    flow1: '',
    flow2: '',
    flow3: ''
  });
  const [params, setParams] = useState({
    ps1: '10',
    ps2: '60',
    tur: '1.8267',
    tds1_raw1: '800',
    tds1_raw2: '900',
    tds1_temp: '25',
    tds2_raw1: '40',
    tds2_raw2: '50',
    tds2_temp: '26',
    tds3_raw1: '140',
    tds3_raw2: '150',
    tds3_temp: '27',
    leakage: '1',
    hps: '0',
    flow1: '150',
    flow2: '50',
    flow3: '100'
  });
  const [loading, setLoading] = useState(false);
  const [ntuToVoltage, setNtuToVoltage] = useState({});
  const { token } = useUserContext();

  // NOTE: We previously tracked whether user was editing NTU using state,
  // but the editing state was never used elsewhere in the component.
  // Remove the unused state variable to avoid ESLint warnings/errors.
  
  // WebSocket connection ref
  const ws = useRef(null);
  
  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
  console.log("Before: ", timestamp);
  if (!timestamp) return '';

  const pad = (n) => n.toString().padStart(2, '0');

  const formatWithOffset = (date) => {
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    const offset = -date.getTimezoneOffset(); // in minutes
    const offsetSign = offset >= 0 ? '+' : '-';
    const offsetHours = pad(Math.floor(Math.abs(offset) / 60));
    const offsetMinutes = pad(Math.abs(offset) % 60);
    const offsetFormatted = `${offsetSign}${offsetHours}:${offsetMinutes}`;

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}${offsetFormatted}`;
  };

  try {
    // ISO format — already handled
    if (timestamp.includes('T') && timestamp.includes(':')) {
      const date = new Date(timestamp);
      return formatWithOffset(date);
    }

    if (/^\d+$/.test(timestamp)) {
      const date = new Date(parseInt(timestamp) * 1000);
      return formatWithOffset(date);
    }

    if (/^\d+\.?\d*$/.test(timestamp)) {
      const date = new Date(parseFloat(timestamp));
      return formatWithOffset(date);
    }

    return timestamp;
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return timestamp;
  }
};

  // Load and parse NTU calibration data from JSON
  useEffect(() => {
    const parseJSONData = (jsonData) => {
      try {
        console.log('Raw JSON data type:', typeof jsonData);
        
        if (!jsonData || !Array.isArray(jsonData)) {
          console.error('Invalid or empty JSON data');
          return {};
        }
        
        const ntuMap = {};
        let validEntries = 0;
        
        // Skip header row (index 0)
        for (let i = 1; i < jsonData.length; i++) {
          try {
            const entry = jsonData[i];
            if (!entry || !entry.null || !Array.isArray(entry.null) || entry.null.length < 3) {
              console.warn(`Skipping invalid entry at index ${i}:`, entry);
              continue;
            }
            
            const [ntuStr, voltageStr] = entry.null;
            const ntu = parseFloat(ntuStr);
            const voltage = parseFloat(voltageStr);
            
            if (!isNaN(ntu) && !isNaN(voltage)) {
              ntuMap[ntu] = voltage;
              validEntries++;
              
              // Log first few mappings for verification
              if (validEntries <= 5) {
                console.log(`Mapped NTU ${ntu} to voltage: ${voltage}`);
              }
            } else {
              console.warn(`Skipping invalid values at index ${i}:`, { ntu: ntuStr, voltage: voltageStr });
            }
          } catch (error) {
            console.warn(`Error processing entry at index ${i}:`, error);
          }
        }
        
        console.log(`Successfully mapped ${validEntries} valid NTU values`);
        if (validEntries === 0) {
          console.error('No valid NTU mappings found in JSON');
        } else {
          console.log('Sample of first 5 NTU mappings:');
          const sample = Object.entries(ntuMap).slice(0, 5);
          sample.forEach(([ntu, voltage]) => console.log(`  ${ntu} -> ${voltage}`));
        }
        
        return ntuMap;
        
      } catch (error) {
        console.error('Error in parseJSONData:', error);
        return {};
      }
    };
    
    // Check if we have JSON data
    if (!ntuCalibrationData) {
      console.error('ntuCalibrationData is undefined or empty');
      return;
    }
    
    console.log('Starting JSON parsing...');
    const parsedMap = parseJSONData(ntuCalibrationData);
    
    if (Object.keys(parsedMap).length > 0) {
      console.log('Setting NTU to voltage mapping with', Object.keys(parsedMap).length, 'entries');
      setNtuToVoltage(parsedMap);
    } else {
      console.error('Failed to parse any valid NTU mappings from JSON');
    }
  }, []); // ntuCalibrationData is static import; run once on mount
  
  // Find NTU value for a given voltage
  const findNtuForVoltage = (voltage) => {
    const entries = Object.entries(ntuToVoltage);
    for (const [ntu, v] of entries) {
      if(Math.abs(parseFloat(v) - Math.abs(voltage) < 0.0001)) {
        return ntu;
      }
    }
    return null;
  };

  // Handle device selection change
  const handleDeviceChange = (e) => {
    const deviceId = e.target.value;
    setSelectedDevice(deviceId);
    
    // Close any existing WebSocket connection
    if (ws.current) {
      ws.current.close();
    }
    
    if (deviceId) {
      // Establish new WebSocket connection for the selected device
      connectWebSocket(deviceId);
    }
  };

  // WebSocket connection function
  const connectWebSocket = (deviceId) => {
    // Connect to the main WebSocket endpoint (not device-specific URL)
    const wsUrl = `${process.env.REACT_APP_API_WS_URL || 'ws://localhost:5000'}/ws`;
    ws.current = new WebSocket(wsUrl);

    ws.current.onopen = () => {
      console.log(`WebSocket connected for device ${deviceId}`);
      // Send action message to backend to start streaming
      ws.current.send(JSON.stringify({
        action: "testdevice_realtime",
        deviceId,
        token // include token if backend expects it
      }));
    };

    ws.current.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        console.log("Received message:", msg);
        if (msg.type === "testdevice_update" && Array.isArray(msg.data) && msg.data.length > 0) {
          // Use the latest data row
          const data = msg.data[0];
          setRealTimeData({
            ts: formatTimestamp(data.ts_test) || '',
            ps1: data.ps1_test?.toString() || '',
            ps2: data.ps2_test?.toString() || '',
            tur: data.tur_test?.toString() || '',
            tds1_raw1: data.tds1_raw1_test?.toString() || '',
            tds1_raw2: data.tds1_raw2_test?.toString() || '',
            tds1_temp: data.tds1_temp_test?.toString() || '',
            tds2_raw1: data.tds2_raw1_test?.toString() || '',
            tds2_raw2: data.tds2_raw2_test?.toString() || '',
            tds2_temp: data.tds2_temp_test?.toString() || '',
            tds3_raw1: data.tds3_raw1_test?.toString() || '',
            tds3_raw2: data.tds3_raw2_test?.toString() || '',
            tds3_temp: data.tds3_temp_test?.toString() || '',
            leakage: data.leakage_test?.toString() || '',
            hps: data.hps_test?.toString() || '',
            flow1: data.flow1_test?.toString() || '',
            flow2: data.flow2_test?.toString() || '',
            flow3: data.flow3_test?.toString() || ''
          });
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.current.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.current.onclose = () => {
      console.log('WebSocket disconnected');
    };
  };

  // Handle NTU input change
  const handleNtuChange = (e) => {
    const inputValue = e.target.value;
    
    // Allow empty input (backspace/delete)
    if (inputValue === '') {
      setParams(prev => ({
        ...prev,
        tur: '',
        ntuValue: ''
      }));
      return;
    }
    
    // Only allow numeric input
    if (!/^\d*$/.test(inputValue)) {
      return; // Don't update if non-numeric
    }
    
    const ntuValue = parseInt(inputValue, 10);
    console.log('Looking up NTU value:', ntuValue, 'in mapping');
    
    // Check if mapping is loaded
    if (Object.keys(ntuToVoltage).length === 0) {
      console.error('NTU to voltage mapping is not loaded yet');
      return;
    }
    
    // Find the voltage for the given NTU value
    const voltage = ntuToVoltage[ntuValue];
    
    if (voltage === undefined) {
      console.warn(`No voltage mapping found for NTU value: ${ntuValue}`);
      // Keep the input value but don't update the voltage
      setParams(prev => ({
        ...prev,
        ntuValue: inputValue
      }));
      return;
    }
    
    console.log(`Found voltage ${voltage} for NTU ${ntuValue}`);
    setParams(prev => ({
      ...prev,
      tur: voltage.toString(),
      ntuValue: inputValue // Keep the input as string to allow partial editing
    }));
  };

  const handleParamChange = (e) => {
    const { name, value } = e.target;
    setParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Get min and max NTU values from the mapping
  const getNtuRange = () => {
    const ntuValues = Object.keys(ntuToVoltage).map(Number).filter(n => !isNaN(n));
    return {
      min: Math.min(...ntuValues),
      max: Math.max(...ntuValues)
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDevice) {
      toast.error('Please select a device');
      return;
    }
    
    // Check if NTU value is within valid range
    if (params.ntuValue) {
      const { min, max } = getNtuRange();
      const currentNtu = parseInt(params.ntuValue, 10);
      
      if (isNaN(currentNtu) || currentNtu < min || currentNtu > max) {
        toast.error(`NTU value must be between ${min} and ${max}`);
        return;
      }
      
      // Double check that the NTU value exists in the mapping
      if (ntuToVoltage[currentNtu] === undefined) {
        toast.error(`No calibration data found for NTU value: ${currentNtu}`);
        return;
      }
    }

    setLoading(true);
    const toastId = toast.loading('Updating device parameters...');
    
    try {
      const deviceId = `gaugeTest_${selectedDevice}`;
      const payload = {
        device_id: deviceId,
        ps1: parseFloat(params.ps1) || 0,
        ps2: parseFloat(params.ps2) || 0,
        tur: parseFloat(params.tur) || 0,
        tds1_raw1: parseFloat(params.tds1_raw1) || 0,
        tds1_raw2: parseFloat(params.tds1_raw2) || 0,
        tds1_temp: parseFloat(params.tds1_temp) || 0,
        tds2_raw1: parseFloat(params.tds2_raw1) || 0,
        tds2_raw2: parseFloat(params.tds2_raw2) || 0,
        tds2_temp: parseFloat(params.tds2_temp) || 0,
        tds3_raw1: parseFloat(params.tds3_raw1) || 0,
        tds3_raw2: parseFloat(params.tds3_raw2) || 0,
        tds3_temp: parseFloat(params.tds3_temp) || 0,
        leakage: parseInt(params.leakage) || 0,
        hps: parseInt(params.hps) || 0,
        flow1: parseFloat(params.flow1) || 0,
        flow2: parseFloat(params.flow2) || 0,
        flow3: parseFloat(params.flow3) || 0
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/devices/params`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update device parameters');
      }
      
      toast.success('Device parameters updated successfully!', { id: toastId });
    } catch (error) {
      console.error('Error updating device parameters:', error);
      toast.error(error.message || 'Failed to update device parameters. Please try again.', { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  // Clean up WebSocket on component unmount
  useEffect(() => {
    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, []);

  const inputStyles = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '8px',
    border: '2px solid #e3f2fd',
    fontSize: '14px',
    transition: 'all 0.3s ease',
    backgroundColor: '#fafafa',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const readOnlyInputStyles = {
    ...inputStyles,
    backgroundColor: '#f0f0f0',
    color: '#666',
    cursor: 'not-allowed'
  };

  const renderInput = (label, name, type = 'text', isNtu = false, readOnly = false) => (
    <div style={{ marginBottom: '16px' }}>
      <label style={{
        display: 'block',
        marginBottom: '8px',
        fontSize: '16px',
        fontWeight: '600',
        color: '#1565c0'
      }}>
        {label}
        {isNtu && (
          <span style={{
            fontSize: '12px',
            color: '#666',
            marginLeft: '8px',
            fontWeight: 'normal'
          }}>
            (Enter 0-5907)
          </span>
        )}
      </label>
      <input
        type={type}
        name={name}
        value={isNtu ? (params.ntuValue || '') : readOnly ? realTimeData[name] : params[name]}
        onChange={isNtu ? handleNtuChange : readOnly ? undefined : handleParamChange}
        style={readOnly ? readOnlyInputStyles : inputStyles}
        onFocus={(e) => {
          // Optional focus styling (no editing state tracking required)
          if (!readOnly) {
            e.target.style.borderColor = '#2196f3';
            e.target.style.backgroundColor = '#fff';
            e.target.style.boxShadow = '0 0 0 3px rgba(33, 150, 243, 0.1)';
          }
        }}
        onBlur={(e) => {
          // Optional blur styling (no editing state tracking required)
          if (!readOnly) {
            e.target.style.borderColor = '#e3f2fd';
            e.target.style.backgroundColor = '#fafafa';
            e.target.style.boxShadow = 'none';
          }
        }}
        readOnly={readOnly}
        step="any"
        min={isNtu ? '0' : undefined}
        max={isNtu ? '5907' : undefined}
      />
      {isNtu && params.ntuValue && (
        <div style={{
          fontSize: '12px',
          color: '#666',
          marginTop: '4px'
        }}>
          Voltage: {params.tur} V
        </div>
      )}
      {/* Show real-time data below each input */}
      {!isNtu && realTimeData[name] && (
        <div style={{
          fontSize: '12px',
          color: '#666',
          marginTop: '4px'
        }}>
          Real-time: {realTimeData[name]}
          {name === 'tur' && (
            <span>
              {` (NTU: ${findNtuForVoltage(parseFloat(realTimeData.tur)) || 'N/A'})`}
            </span>
          )}
        </div>
      )}
    </div>
  );

  const renderSection = (title, readOnly = false) => (
    <div style={{
      backgroundColor: '#fff',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 4px 20px rgba(33, 150, 243, 0.1)',
      border: '1px solid #e3f2fd',
      height: '100%'
    }}>
      <h3 style={{
        fontSize: '20px',
        fontWeight: '700',
        color: readOnly ? '#1565c0' : '#2196f3',
        margin: '0 0 20px 0',
        textAlign: 'center',
        borderBottom: `2px solid ${readOnly ? '#1565c0' : '#2196f3'}`,
        paddingBottom: '10px'
      }}>
        {title}
      </h3>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px'
      }}>
        {renderInput('Pressure Sensor 1 (PS1)', 'ps1', 'number', false, readOnly)}
        {renderInput('Pressure Sensor 2 (PS2)', 'ps2', 'number', false, readOnly)}
        {renderInput('Timestamp', 'ts', 'text', false, true)}
        {renderInput('Turbidity (Voltage)', 'tur', 'number', false, true)}
        {!readOnly && renderInput('Turbidity (NTU)', 'ntuValue', 'number', true, false)}
        {renderInput('TDS1 Raw Reading 1', 'tds1_raw1', 'number', false, readOnly)}
        {renderInput('TDS1 Raw Reading 2', 'tds1_raw2', 'number', false, readOnly)}
        {renderInput('TDS1 Temperature', 'tds1_temp', 'number', false, readOnly)}
        {renderInput('TDS2 Raw Reading 1', 'tds2_raw1', 'number', false, readOnly)}
        {renderInput('TDS2 Raw Reading 2', 'tds2_raw2', 'number', false, readOnly)}
        {renderInput('TDS2 Temperature', 'tds2_temp', 'number', false, readOnly)}
        {renderInput('TDS3 Raw Reading 1', 'tds3_raw1', 'number', false, readOnly)}
        {renderInput('TDS3 Raw Reading 2', 'tds3_raw2', 'number', false, readOnly)}
        {renderInput('TDS3 Temperature', 'tds3_temp', 'number', false, readOnly)}
        {renderInput('Flow Rate 1', 'flow1', 'number', false, readOnly)}
        {renderInput('Flow Rate 2', 'flow2', 'number', false, readOnly)}
        {renderInput('Flow Rate 3', 'flow3', 'number', false, readOnly)}
      </div>
      
      {/* Toggle Controls */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginTop: '20px'
      }}>
        <div>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '16px',
            fontWeight: '600',
            color: '#1565c0'
          }}>
            Leakage Detection
          </label>
          <select
            name="leakage"
            value={readOnly ? realTimeData.leakage : params.leakage}
            onChange={readOnly ? undefined : handleParamChange}
            style={{
              ...(readOnly ? readOnlyInputStyles : inputStyles),
              cursor: readOnly ? 'not-allowed' : 'pointer'
            }}
            disabled={readOnly}
          >
            <option value="1">Enabled</option>
            <option value="0">Disabled</option>
          </select>
          {realTimeData.leakage && (
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '4px'
            }}>
              Real-time: {realTimeData.leakage === '1' ? 'Enabled' : 'Disabled'}
            </div>
          )}
        </div>

        <div>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '16px',
            fontWeight: '600',
            color: '#1565c0'
          }}>
            High Pressure Switch (HPS)
          </label>
          <select
            name="hps"
            value={readOnly ? realTimeData.hps : params.hps}
            onChange={readOnly ? undefined : handleParamChange}
            style={{
              ...(readOnly ? readOnlyInputStyles : inputStyles),
              cursor: readOnly ? 'not-allowed' : 'pointer'
            }}
            disabled={readOnly}
          >
            <option value="1">On</option>
            <option value="0">Off</option>
          </select>
          {realTimeData.hps && (
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '4px'
            }}>
              Real-time: {realTimeData.hps === '1' ? 'On' : 'Off'}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f5f9ff', 
      paddingTop: '80px'
    }}>
      <div style={{ 
        maxWidth: '1400px', 
        margin: '0 auto', 
        padding: '0 20px'
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '32px',
          boxShadow: '0 4px 20px rgba(33, 150, 243, 0.1)',
          border: '1px solid #e3f2fd'
        }}>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '700',
            color: '#1565c0',
            margin: '0 0 8px 0',
            textAlign: 'center'
          }}>
            Device Parameters Configuration
          </h1>
          <p style={{
            fontSize: '16px',
            color: '#64b5f6',
            margin: '0',
            textAlign: 'center'
          }}>
            {selectedDevice ? `Connected to device: ${selectedDevice}` : 'Select a device to begin'}
          </p>
        </div>

        {/* Device Selection */}
        <div style={{ 
          marginBottom: '32px',
          backgroundColor: '#fff',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 4px 20px rgba(33, 150, 243, 0.1)',
          border: '1px solid #e3f2fd'
        }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '16px',
            fontWeight: '600',
            color: '#1565c0'
          }}>
            Select Device
          </label>
          <select
            value={selectedDevice}
            onChange={handleDeviceChange}
            style={{
              ...inputStyles,
              cursor: 'pointer'
            }}
            required
          >
            <option value="">Choose a device...</option>
            <option value="84CCA850235C">84CCA850235C - Gauge Test Device</option>
            <option value="E831CD803100">E831CD803100 - Gauge Test Device</option>
            <option value="C8F09E317B40">C8F09E317B40 - Gauge Test Device</option>
          </select>
        </div>

        {/* Two Column Layout */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '32px',
          marginBottom: '32px'
        }}>
          {/* Editable Parameters Column */}
          <div>
            {renderSection('Update Parameters')}
            
            {/* Submit Button */}
            <div style={{ 
              textAlign: 'center',
              marginTop: '24px'
            }}>
              <button
                onClick={handleSubmit}
                disabled={loading || !selectedDevice}
                style={{
                  padding: '16px 48px',
                  backgroundColor: loading ? '#90caf9' : !selectedDevice ? '#90caf9' : '#2196f3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: loading || !selectedDevice ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  boxShadow: loading || !selectedDevice ? 'none' : '0 4px 12px rgba(33, 150, 243, 0.3)',
                  minWidth: '200px'
                }}
                onMouseEnter={(e) => {
                  if (!loading && selectedDevice) {
                    e.target.style.backgroundColor = '#1976d2';
                    e.target.style.boxShadow = '0 6px 16px rgba(33, 150, 243, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading && selectedDevice) {
                    e.target.style.backgroundColor = '#2196f3';
                    e.target.style.boxShadow = '0 4px 12px rgba(33, 150, 243, 0.3)';
                  }
                }}
              >
                {loading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid #fff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginRight: '8px'
                    }}></div>
                    Updating...
                  </div>
                ) : (
                  'Update Parameters'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        input[type="number"]::-webkit-outer-spin-button,
        input[type="number"]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        
        input[type="number"] {
          -moz-appearance: textfield;
        }
      `}</style>
    </div>
  );
};

export default DeviceParamsForm;